import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import SectionCard from "../../components/account/SectionCard";
import { getMyApplications, getApplicationTimeline } from "../../services/notificationService";
import { ListSkeleton } from "../../components/skeletons/ShopperSkeletons";

const statusBadge = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
  if (s === "REJECTED") return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
  if (s === "IN_REVIEW") return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
  if (s === "CANCELLED") return "bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300";
  return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
};

const EVENT_LABELS = {
  submitted: "Application Submitted",
  resubmitted: "Resubmitted",
  approved: "Approved",
  rejected: "Rejected",
  info_requested: "More Info Requested",
  cancelled: "Cancelled",
};

const EVENT_DOT_COLORS = {
  submitted: "bg-blue-500",
  resubmitted: "bg-indigo-500",
  approved: "bg-emerald-500",
  rejected: "bg-red-500",
  info_requested: "bg-amber-500",
  cancelled: "bg-neutral-400",
};

/** Expandable timeline for a single application */
function ApplicationTimeline({ applicationId }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadTimeline = useCallback(async () => {
    if (events !== null) { setOpen((o) => !o); return; }
    setLoading(true);
    try {
      const res = await getApplicationTimeline(applicationId);
      setEvents(res?.data?.events || []);
      setOpen(true);
    } catch { setEvents([]); setOpen(true); }
    finally { setLoading(false); }
  }, [applicationId, events]);

  return (
    <div>
      <button
        type="button"
        onClick={loadTimeline}
        className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
      >
        {loading ? "Loading..." : open ? "Hide Timeline" : "View Timeline"}
      </button>

      {loading ? <div className="mt-2"><ListSkeleton rows={2} label="Loading application timeline" /></div> : null}

      {open && events && (
        <div className="mt-2 relative pl-4 border-l-2 border-neutral-200 dark:border-neutral-700 space-y-2">
          {events.length === 0 ? (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">No events recorded yet.</p>
          ) : events.map((ev) => (
            <div key={ev.id} className="relative">
              <span className={`absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-neutral-900 ${EVENT_DOT_COLORS[ev.event_type] || 'bg-neutral-400'}`} />
              <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                {new Date(ev.created_at).toLocaleString()}
                {ev.actor_role && <span className="ml-1 uppercase text-[9px] font-semibold text-neutral-400 dark:text-neutral-500">({ev.actor_role})</span>}
              </div>
              <p className="text-xs text-neutral-700 dark:text-neutral-300">
                <span className="font-semibold">{EVENT_LABELS[ev.event_type] || ev.event_type}</span>
                {ev.summary && <span className="ml-1">— {ev.summary}</span>}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MyApplicationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadPending, setInitialLoadPending] = useState(true);
  const [error, setError] = useState("");

  const fetchRows = async ({ initial = false } = {}) => {
    try {
      setLoading(true);
      const res = await getMyApplications();
      setRows(res?.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.msg || "Failed to load applications.");
    } finally {
      setLoading(false);
      if (initial) setInitialLoadPending(false);
    }
  };

  useEffect(() => {
    fetchRows({ initial: true });
  }, []);

  const subtitle = useMemo(() => {
    if (loading) return "Loading applications...";
    return `${rows.length} application${rows.length === 1 ? "" : "s"}`;
  }, [loading, rows.length]);

  return (
    <SectionCard
      title="My Applications"
      subtitle={subtitle}
      action={
        <button
          type="button"
          onClick={fetchRows}
          disabled={loading}
          className="rounded-lg border border-neutral-300 px-3 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          Refresh
        </button>
      }
    >
      {initialLoadPending ? (
        <ListSkeleton rows={3} label="Loading business applications" />
      ) : error ? (
        <div className="p-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-sm text-neutral-500 dark:text-neutral-400">
          No business applications yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const canResubmit = Boolean(r.requested_info);
            return (
              <div
                key={r.id}
                className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                      {r.business_name}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                      Submitted: {r.created_at ? new Date(r.created_at).toLocaleString() : "N/A"}
                    </p>
                    {r.last_resubmitted_at && (
                      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        Last resubmitted: {new Date(r.last_resubmitted_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusBadge(r.status)}`}>
                    {r.status}
                  </span>
                </div>

                {(r.review_notes || r.rejection_reason) && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800/40 dark:bg-amber-950/30">
                    <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 mb-0.5">Reviewer Notes</p>
                    <p className="text-xs text-amber-900 dark:text-amber-200 whitespace-pre-wrap">
                      {r.review_notes || r.rejection_reason}
                    </p>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="rounded-md bg-neutral-100 px-2 py-1 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                    Resubmits: {r.resubmit_count || 0}
                  </span>
                  {canResubmit && (
                    <Link
                      to={`/business/resubmit/${r.id}`}
                      className="rounded-md bg-blue-600 px-3 py-1.5 font-semibold text-white hover:bg-blue-700"
                    >
                      Review & Resubmit
                    </Link>
                  )}
                </div>

                <div className="mt-3 border-t border-neutral-100 dark:border-neutral-800 pt-2">
                  <ApplicationTimeline applicationId={r.id} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
